import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User as FirebaseUser } from 'firebase/auth';

/**
 * Minimal user DTO for persistence.
 * SECURITY: Only store non-sensitive profile data. Never persist tokens.
 * Firebase manages its own token persistence separately.
 */
interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  emailVerified: boolean;
  passkeyRegistered: boolean;
  /** True for guest (anonymous Firebase) sessions — gates the convert card + buy block. */
  isAnonymous: boolean;
}

/**
 * Sanitize Firebase user to minimal DTO.
 * Strips all sensitive data (tokens, provider data, metadata).
 */
function sanitizeUser(firebaseUser: FirebaseUser | null): User | null {
  if (!firebaseUser) return null;
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    emailVerified: firebaseUser.emailVerified,
    passkeyRegistered: false,
    isAnonymous: firebaseUser.isAnonymous,
  };
}

interface AuthState {
  user: User | null;
  isLoading: boolean;
  isLoggedIn: boolean;
  error: string | null;
  signedOutElsewhere: boolean;
  /** True once zustand has rehydrated from localStorage */
  hasHydrated: boolean;

  // Actions
  /** Sets user from Firebase user object (sanitizes to minimal DTO) */
  setUser: (user: FirebaseUser | null) => void;
  updateProfile: (updates: Partial<User>) => void;
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  signOut: () => void;
  markSignedOutElsewhere: () => void;
  clearSignedOutElsewhere: () => void;
  setHasHydrated: (hydrated: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: true,
      isLoggedIn: false,
      error: null,
      signedOutElsewhere: false,
      hasHydrated: false,

      setUser: (firebaseUser) =>
        set((state) => {
          const sanitized = sanitizeUser(firebaseUser);
          return {
            user: sanitized
              ? {
                  ...sanitized,
                  passkeyRegistered:
                    state.user?.uid === sanitized.uid
                      ? (state.user?.passkeyRegistered ?? false)
                      : false,
                }
              : null,
            isLoggedIn: !!firebaseUser,
            isLoading: false,
            error: null,
            signedOutElsewhere: false,
          };
        }),

      updateProfile: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      signOut: () =>
        set({
          user: null,
          isLoggedIn: false,
          isLoading: false,
          error: null,
          signedOutElsewhere: false,
        }),
      markSignedOutElsewhere: () =>
        set({
          signedOutElsewhere: true,
        }),
      clearSignedOutElsewhere: () =>
        set({
          signedOutElsewhere: false,
        }),
      setHasHydrated: (hydrated) => set({ hasHydrated: hydrated }),
    }),
    {
      name: 'auth-storage',
      version: 4, // v4: add isAnonymous
      partialize: (state) => ({
        user: state.user,
        isLoggedIn: state.isLoggedIn,
        signedOutElsewhere: state.signedOutElsewhere,
      }),
      onRehydrateStorage: () => (state) => {
        // Called after rehydration completes
        state?.setHasHydrated(true);
      },
      migrate: (persistedState, version) => {
        // Migration from v0 (no version): Clear any persisted tokens from old format
        if (version === 0) {
          const state = persistedState as { user?: unknown; isLoggedIn?: boolean };
          // If user object has token-related fields, sanitize it
          if (state.user && typeof state.user === 'object') {
            const oldUser = state.user as Record<string, unknown>;
            // Check for token fields that shouldn't be persisted
            if (
              'stsTokenManager' in oldUser ||
              'accessToken' in oldUser ||
              'refreshToken' in oldUser
            ) {
              // Clear the user - they'll need to re-auth (Firebase will restore session)
              return { user: null, isLoggedIn: false, signedOutElsewhere: false };
            }
            // Otherwise sanitize to only allowed fields
            return {
              user: {
                uid: oldUser.uid as string,
                email: (oldUser.email as string | null) ?? null,
                displayName: (oldUser.displayName as string | null) ?? null,
                photoURL: (oldUser.photoURL as string | null) ?? null,
                emailVerified: (oldUser.emailVerified as boolean) ?? false,
                passkeyRegistered: false,
                isAnonymous: false,
              },
              isLoggedIn: state.isLoggedIn ?? false,
              signedOutElsewhere: false,
            };
          }
        } else if (version < 2) {
          // Migration to v2: Default signedOutElsewhere to false if missing
          const state = persistedState as AuthState;
          return { ...state, signedOutElsewhere: state.signedOutElsewhere ?? false };
        } else if (version < 3) {
          const state = persistedState as AuthState;
          const user = state.user
            ? { ...state.user, passkeyRegistered: state.user.passkeyRegistered ?? false }
            : null;
          return { ...state, user };
        } else if (version < 4) {
          // v4: add isAnonymous. Persisted users are email accounts → false.
          const state = persistedState as AuthState;
          const user = state.user
            ? { ...state.user, isAnonymous: state.user.isAnonymous ?? false }
            : null;
          return { ...state, user };
        }
        return persistedState as AuthState;
      },
    }
  )
);
